import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";

import { ownerProposalFormSchema, type OwnerProposalFormValues } from "../model/schemas";
import { getStationName } from "../model/utils";

import { StyledTableCell, StyledTableHead } from "./styled-table-components";

import type { OrderDetailResponseDTO } from "@/common/api";
import {
  UnloadingDirectionDTOTariffRoad,
  useGetAllOffers,
  useSaveAllOffers,
  OrderDetailResponseDTOStatus,
} from "@/common/api";
import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
  Input,
  Table,
  TableBody,
  TableHeader,
  TableRow,
} from "@/common/components";
import { useAuth, useToast } from "@/common/hooks";
import { TARIFF_LABELS } from "@/common/model";

interface OwnerProposalFormProps {
  order: OrderDetailResponseDTO;
}

export const OwnerProposalForm = ({ order }: OwnerProposalFormProps) => {
  const { toast } = useToast();
  const {
    auth: { isAdmin },
  } = useAuth();

  const hasAccess = !isAdmin && Boolean(order?.isOwner);

  const {
    data: offersData,
    isLoading,
    refetch,
  } = useGetAllOffers(
    { orderId: order.id! },
    {
      query: {
        refetchInterval: 1 * 60 * 1000,
      },
    },
  );

  const { mutateAsync: mutateSaveAllOffersAsync, isPending: isSaveAllOffersPending } =
    useSaveAllOffers();

  // Преобразуем данные из API в формат для формы
  const tableData = useMemo(() => {
    if (!offersData?.data?.payload?.directions) return [];

    const directions = offersData.data.payload.directions.sort(
      (a, b) => (a.index ?? 0) - (b.index ?? 0),
    );

    return directions.map((direction) => ({
      id: String(direction.index ?? ""),
      roadStation: getStationName(direction.unloadingRoad, direction.unloadingStation) ?? "-",
      unit: direction.tariffRoad ? TARIFF_LABELS[direction.tariffRoad] : "-",
      tariffRoad: direction.tariffRoad as UnloadingDirectionDTOTariffRoad | undefined,
    }));
  }, [offersData]);

  // Формируем участников из API
  const participants = useMemo(() => {
    if (!offersData?.data?.payload?.participants) return [];

    return offersData.data.payload.participants.map((participant) => {
      const values =
        participant.offers?.reduce(
          (acc, offer) => {
            if (offer) {
              acc[String(offer?.indexNumber)] = String(offer?.wagonTariff ?? 0);
            }

            return acc;
          },
          {} as Record<string, string>,
        ) ?? {};

      return {
        id: participant.participantNumber,
        name:
          isAdmin && participant.companyName?.trim()
            ? participant.companyName
            : `Участник ${participant.participantNumber ?? ""}`,
        values,
        countWagonsOwner: participant.countWagonsOwner ?? 0,
      };
    });
  }, [offersData, isAdmin]);

  const form = useForm<OwnerProposalFormValues>({
    resolver: zodResolver(ownerProposalFormSchema),
    mode: "onChange",
    shouldUnregister: false,
    defaultValues: {
      proposals: {},
      tariffRoad: {},
    },
  });

  const totalWagons = useMemo(() => offersData?.data?.payload?.totalWagons ?? 0, [offersData]);

  const hasRetradingRound = useMemo(
    () =>
      offersData?.data?.payload?.allowRetradingRound &&
      order.status === OrderDetailResponseDTOStatus.REBIDDING,
    [offersData, order],
  );

  const handleSubmit = (data: OwnerProposalFormValues) => {
    const offers = Object.entries(data.proposals ?? {}).map(([index, value]) => ({
      indexNumber: Number(index),
      wagonTariff: value ? Number(value) : undefined,
    }));

    mutateSaveAllOffersAsync({
      data: {
        orderId: order.id!,
        offers,
      },
    })
      .then(() => {
        return refetch();
      })
      .then(() => {
        toast({
          title: "Ответ отправлен",
        });
      })
      .catch((error: unknown) => {
        toast({
          variant: "destructive",
          title: "Ошибка",
          description: error instanceof Error ? error.message : "Не удалось отправить ответ",
        });
      });
  };

  useEffect(() => {
    if (tableData.length) {
      const proposals = tableData.reduce(
        (acc, row) => {
          acc[row.id] = undefined;
          return acc;
        },
        {} as Record<string, number | undefined>,
      );

      const tariffRoad = tableData.reduce(
        (acc, row) => ({
          ...acc,
          [row.id]: row.tariffRoad,
        }),
        {} as Record<string, UnloadingDirectionDTOTariffRoad | undefined>,
      );

      form.reset(
        {
          proposals,
          tariffRoad,
        },
        {
          keepErrors: true,
          keepValues: true,
        },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableData]);

  if (isLoading) {
    return (
      <div className="mb-6">
        <p className="text-Text-secondary">Загрузка данных...</p>
      </div>
    );
  }

  if (!offersData?.data?.payload || !tableData.length) {
    return null;
  }

  return (
    <Form {...form}>
      <form
        onSubmit={hasAccess ? form.handleSubmit(handleSubmit) : (e) => e.preventDefault()}
        noValidate
      >
        <div className="mb-5 md:mb-6 overflow-hidden">
          <div className="p-3 md:p-4">
            <h2 className="text-2xl font-semibold text-Text-main leading-7">
              Ответ на предложение
            </h2>

            {Number(offersData?.data?.payload?.round) > 0 && (
              <p className="text-base md:text-lg text-Text-secondary mt-3 md:mt-4">
                Переторжка №{offersData?.data?.payload?.round}
              </p>
            )}
          </div>

          <Table className="min-w-[600px] md:min-w-full mb-3 md:mb-0">
            <TableHeader>
              <TableRow>
                <StyledTableHead>Дорога/Станция выгрузки</StyledTableHead>

                <StyledTableHead>Ваше предложение</StyledTableHead>

                {participants.map((participant) => (
                  <StyledTableHead key={participant.id}>{participant.name}</StyledTableHead>
                ))}

                <StyledTableHead>Тип тарифа</StyledTableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {tableData.map((row) => (
                <TableRow key={row.id}>
                  <StyledTableCell>{row.roadStation}</StyledTableCell>

                  <StyledTableCell>
                    <FormField
                      control={form.control}
                      name={`proposals.${row.id}`}
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <Input
                              {...field}
                              size="sm"
                              type="number"
                              className="w-full max-w-[120px]"
                              disabled={!hasRetradingRound}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <input
                      type="hidden"
                      {...form.register(`tariffRoad.${row.id}`)}
                      value={row.tariffRoad || ""}
                      disabled
                    />
                  </StyledTableCell>

                  {participants.map((participant) => (
                    <StyledTableCell key={participant.id}>
                      {participant.values[row.id] ?? "-"}
                    </StyledTableCell>
                  ))}

                  <StyledTableCell>{row.unit}</StyledTableCell>
                </TableRow>
              ))}
              <TableRow>
                <StyledTableCell>
                  <b>Итого вагонов</b>
                </StyledTableCell>

                <StyledTableCell>
                  <b>{totalWagons}</b>
                </StyledTableCell>

                {participants.map((participant) => (
                  <StyledTableCell key={participant.id}>
                    <b>{participant.countWagonsOwner ?? 0}</b>
                  </StyledTableCell>
                ))}

                <StyledTableCell>
                  <b>шт.</b>
                </StyledTableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>

        {hasAccess && (
          <div className="flex justify-end mb-6">
            <Button
              type="submit"
              disabled={isSaveAllOffersPending || !hasRetradingRound}
              className="bg-Main-orange hover:bg-Main-orange/90 text-white rounded-md px-6 py-2 w-full md:w-auto"
            >
              {isSaveAllOffersPending ? "Отправка..." : "Отправить ответ"}
            </Button>
          </div>
        )}
      </form>
    </Form>
  );
};
